// src/components/subscriptions/AddSubscriptionModal.tsx
// Выдача абонемента клиенту по email: сумму и процент админ задаёт сам.
import React, { useEffect, useState } from "react";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import Field from "../ui/Field";
import Input from "../ui/Input";
import Textarea from "../ui/Textarea";
import Switch from "../ui/Switch";
import {
    AdminCreateSubscriptionPayload,
    SubscriptionPaymentType,
} from "../../services/types/subscriptions.types";

interface AddSubscriptionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (payload: AdminCreateSubscriptionPayload) => Promise<void>;
}

// Быстрые кнопки суммы из условий клуба: подарок, половина, полная цена.
const PRICE_PRESETS = ["0", "10", "15"];

interface FormState {
    email: string;
    price: string;
    discountPercent: string;
    paymentType: SubscriptionPaymentType;
    comment: string;
    sendEmail: boolean;
}

const initialFormState: FormState = {
    email: "",
    price: "15",
    discountPercent: "10",
    paymentType: "rechnung",
    comment: "",
    sendEmail: true,
};

const AddSubscriptionModal: React.FC<AddSubscriptionModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
}) => {
    const [formState, setFormState] = useState<FormState>(initialFormState);
    const [isSaving, setIsSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    // Каждое открытие начинается с чистой формы.
    useEffect(() => {
        if (isOpen) {
            setFormState(initialFormState);
            setFormError(null);
            setIsSaving(false);
        }
    }, [isOpen]);

    const priceValue = Number(formState.price);
    // Подарочный абонемент за 0 € активируется сразу, счёт не выставляется.
    const isFreeGift = !Number.isNaN(priceValue) && priceValue === 0;

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setFormError(null);

        const email = formState.email.trim();

        if (!email) {
            setFormError("Укажите email клиента.");
            return;
        }

        if (Number.isNaN(priceValue) || priceValue < 0) {
            setFormError("Сумма должна быть числом не меньше нуля.");
            return;
        }

        const percentValue = Number(formState.discountPercent);

        if (Number.isNaN(percentValue) || percentValue < 0 || percentValue > 100) {
            setFormError("Процент скидки должен быть от 0 до 100.");
            return;
        }

        setIsSaving(true);

        try {
            await onSubmit({
                email,
                price: priceValue.toFixed(2),
                discount_percent: percentValue.toFixed(2),
                payment_type: formState.paymentType,
                comment: formState.comment.trim() || null,
                send_email: formState.sendEmail,
            });
        } catch (error) {
            setFormError(
                error instanceof Error
                    ? error.message
                    : "Не удалось выдать абонемент.",
            );
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} title="Выдать абонемент" onClose={onClose} width="md">
            <form className="editor-form" onSubmit={handleSubmit}>
                <Field label="Email клиента" htmlFor="subscription-email">
                    <Input
                        id="subscription-email"
                        type="email"
                        value={formState.email}
                        placeholder="kunde@example.com"
                        autoComplete="off"
                        onChange={(event) =>
                            setFormState((prev) => ({
                                ...prev,
                                email: event.target.value,
                            }))
                        }
                    />
                </Field>

                <Field label="Сумма абонемента, €" htmlFor="subscription-price">
                    <Input
                        id="subscription-price"
                        type="number"
                        min={0}
                        step="0.01"
                        value={formState.price}
                        onChange={(event) =>
                            setFormState((prev) => ({
                                ...prev,
                                price: event.target.value,
                            }))
                        }
                    />
                </Field>

                <div className="inline-actions" style={{ marginBottom: "12px" }}>
                    {PRICE_PRESETS.map((preset) => (
                        <Button
                            key={preset}
                            type="button"
                            variant={formState.price === preset ? "primary" : "secondary"}
                            onClick={() =>
                                setFormState((prev) => ({ ...prev, price: preset }))
                            }
                        >
                            {preset} €
                        </Button>
                    ))}
                </div>

                <Field label="Процент скидки на билеты" htmlFor="subscription-percent">
                    <Input
                        id="subscription-percent"
                        type="number"
                        min={0}
                        max={100}
                        step="0.01"
                        value={formState.discountPercent}
                        onChange={(event) =>
                            setFormState((prev) => ({
                                ...prev,
                                discountPercent: event.target.value,
                            }))
                        }
                    />
                </Field>

                {!isFreeGift && (
                    <Field label="Способ оплаты" htmlFor="subscription-payment-type">
                        <select
                            id="subscription-payment-type"
                            className="ui-select"
                            value={formState.paymentType}
                            onChange={(event) =>
                                setFormState((prev) => ({
                                    ...prev,
                                    paymentType: event.target
                                        .value as SubscriptionPaymentType,
                                }))
                            }
                        >
                            <option value="rechnung">По счёту</option>
                            <option value="stripe">Stripe (ссылка на оплату)</option>
                        </select>
                    </Field>
                )}

                <Field label="Комментарий" htmlFor="subscription-comment">
                    <Textarea
                        id="subscription-comment"
                        rows={3}
                        value={formState.comment}
                        placeholder="Необязательно: основание выдачи"
                        onChange={(event) =>
                            setFormState((prev) => ({
                                ...prev,
                                comment: event.target.value,
                            }))
                        }
                    />
                </Field>

                <div className="form-switch-row">
                    <Switch
                        checked={formState.sendEmail}
                        label={
                            isFreeGift
                                ? "Отправить клиенту поздравление с датой завершения"
                                : "Отправить клиенту счёт на почту"
                        }
                        onChange={(checked) =>
                            setFormState((prev) => ({ ...prev, sendEmail: checked }))
                        }
                    />
                </div>

                <p className="card-text">
                    {isFreeGift
                        ? "Сумма 0 € — абонемент активируется сразу на год, счёт не выставляется."
                        : "Абонемент создаётся со статусом «не оплачено» и активируется после оплаты."}
                </p>

                {formError && <div className="form-global-error">{formError}</div>}

                <div className="editor-actions">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={onClose}
                        disabled={isSaving}
                    >
                        Отмена
                    </Button>

                    <Button type="submit" disabled={isSaving}>
                        {isSaving ? "Сохранение..." : "Выдать абонемент"}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default AddSubscriptionModal;
